import { Bounce, Power1, TimelineLite, TimelineMax, TweenMax } from 'gsap';
import { GameAudio } from '../../Config/Audio';
import { ChestSize, IMapChests, MapChestsStates } from '../../Config/MapChests';
import Core from '../../Core/Core';
import SoundManager from '../../Sound/SoundManager';
import { IRewardItem } from '../../State/reducers/score';
import { waitForMs } from '../../Utils/AsyncUtils';
import { RewardSparkle } from '../Animations/RewardSparkle';
import { RewardGroup } from '../PopUps/Win/RewardGroup';
import SpeechBox from '../Shared/SpeechBox';

/**
 * chest displayed on the map
 */
export class MapChest extends PIXI.Sprite {
    public chests: IMapChests;
    public sprite: PIXI.Sprite;
    private size: ChestSize;
    private speechBox: SpeechBox;
    private timeLine: TimelineLite;

    constructor(chests: IMapChests, state: MapChestsStates) {
        super();

        this.chests = chests;
        if (this.chests.position) {
            this.position.set(...this.chests.position);
        }
        this.size = chests.size as ChestSize;
        if (state === MapChestsStates.LOCKED || state === MapChestsStates.UNLOCKED) {
            this.sprite = new PIXI.Sprite(
                PIXI.utils.TextureCache['mapui/chest-' + this.size + '-closed']
            );
            this.sprite.interactive = true;
            this.sprite.buttonMode = true;
        } else {
            this.sprite = new PIXI.Sprite(
                PIXI.utils.TextureCache['mapui/chest-' + this.size + '-open']
            );
        }
        this.sprite.anchor.set(0.5, 1);
        this.sprite.y = this.sprite.height / 2;
        this.sprite.scale.set(1.2);

        this.speechBox = new SpeechBox(true);
        this.speechBox.anchor.set(0.5);
        this.speechBox.x = -this.sprite.width * 0.4;
        this.speechBox.y = -this.sprite.height * 0.3;
        this.speechBox.scale.set(0.8);
        this.speechBox.showTxt(
            Core.local
                .getText('MissionBeatLevel')
                .replace('[X]', '<high>' + chests.levelNum + '</high>')
        );
        this.speechBox.scaleTxt();
        this.speechBox.alpha = 0;

        this.addChild(this.sprite, this.speechBox);

        if (state === MapChestsStates.UNLOCKED) {
            this.unlockedIdleAnimation().catch(err => {
                console.log(['error with chest unlocked', err]);
            });
        } else if (state === MapChestsStates.LOCKED) {
            this.lockedIdleAnimation();
        }
    }

    /**
     * called by the map manager if the chest is beeing clicked on while it is still locked
     * shows a shake of the chest and shows a text saying when the player can open the chest
     */
    public async showClosedAnimation() {
        new TimelineMax({ repeat: 1 })
            .to(this.sprite, 0.05, {
                x: 5,
            })
            .to(this.sprite, 0.1, {
                x: -5,
            })
            .to(this.sprite, 0.05, {
                x: 0,
            });
        TweenMax.to(this.speechBox, 0.2, {
            alpha: 1,
        });
        await waitForMs(1700);
        TweenMax.to(this.speechBox, 0.2, {
            alpha: 0,
        });
    }

    /**
     * called by the map manager when the chest is clicked and its state is unlocked
     * opens the chest and shows its reward
     * @param rewards the items rewarded to the player / contents of the chest
     * @param collectionInProgress the current collection in progress
     * @param collectionProgress the current collection progress
     */
    public async openAnimation(
        rewards: IRewardItem[],
        collectionInProgress: string,
        collectionProgress: number
    ) {
        this.killTweens();
        TweenMax.to(this.sprite.scale, 0.3, {
            y: 0.9,
            repeat: 1,
            yoyo: true,
            onComplete: () => {
                SoundManager.playSound(GameAudio.CHEST_OPEN);
                this.sprite.texture = PIXI.utils.TextureCache['mapui/chest-' + this.size + '-open'];
                const r = new RewardSparkle(2);
                r.anchor.set(0.5);
                this.addChild(r);
                r.play();
                const contents = this.showRewards(
                    rewards,
                    collectionInProgress,
                    collectionProgress
                );
                contents.catch(err => {});

                TweenMax.to(this.sprite.scale, 0.3, {
                    y: 1.2,
                    repeat: 1,
                    yoyo: true,
                });
            },
        });
        this.sprite.interactive = false;
        this.sprite.buttonMode = false;
        await waitForMs(2500);
    }

    /**
     * shows the player the items in the chest
     * @param rewards the items to show
     * @param collectionInProgress the current collection in progress
     * @param collectionProgress the current collection progress
     */
    public async showRewards(
        rewards: IRewardItem[],
        collectionInProgress: string,
        collectionProgress: number
    ) {
        const groups: RewardGroup[] = [];
        rewards.forEach((reward, index) => {
            const rewardGroup = new RewardGroup(
                reward.id,
                reward.val,
                collectionInProgress,
                collectionProgress
            );
            rewardGroup.x = this.sprite.x;
            rewardGroup.y = this.sprite.y;
            const xPos = index === 0 ? this.sprite.width * 0.3 * -1 : this.sprite.width * 0.3;
            const yPos = this.sprite.y - this.sprite.height * 1.15;
            rewardGroup.scale.set(0);
            this.addChild(rewardGroup);
            TweenMax.to(rewardGroup.scale, 0.5, { x: 1.5, y: 1.5, delay: 0.5 * index });
            // added bezier function
            TweenMax.to(rewardGroup, 0.7, {
                bezier: {
                    type: 'soft',
                    values: [
                        { x: rewardGroup.x, y: rewardGroup.y },
                        { x: xPos, y: 0 },
                        { x: xPos, y: yPos },
                    ],
                },
                delay: 0.5 * index,
                ease: Power1.easeIn,
                onComplete: () => {
                    rewardGroup.showFX();
                },
            });
            groups.push(rewardGroup);
        });
        await waitForMs(1800);
        groups.forEach(async element => {
            TweenMax.to(element, 0.2, {
                alpha: 0,
            });
            await waitForMs(200);
            element.destroy();
        });
    }

    /**
     * shows a jumping animation
     * used when the chest is ready to be opened
     */
    private async unlockedIdleAnimation() {
        const scale = this.sprite.scale;
        const position = this.sprite.position;
        this.timeLine = new TimelineMax({ repeat: -1, repeatDelay: 2.5 })
            .to(this.sprite.scale, 0.1, {
                y: scale.y - 0.5,
            })
            .to(this.sprite.scale, 0.1, {
                x: scale.x + 0.2,
                y: scale.y + 0.2,
            })
            .to(
                this.sprite,
                0.4,
                {
                    ease: Power1.easeOut,
                    y: position.y - 30,
                },
                '-=.2'
            )
            .to(
                this.sprite,
                0.6,
                {
                    ease: Bounce.easeOut,
                    y: position.y,
                },
                '-=.4'
            )
            .to(this.sprite.scale, 0.6, {
                ease: Bounce.easeOut,
                x: scale.x,
                y: scale.y,
            });
    }

    /**
     * shows a breathing animation of the chest
     * only shown when the chest is locked
     */
    private lockedIdleAnimation() {
        const scale = this.sprite.scale.y;
        TweenMax.to(this.sprite.scale, 1, {
            ease: Power1.easeOut,
            y: scale + 0.05,
            yoyo: true,
            repeat: -1,
        });
    }

    private killTweens() {
        TweenMax.killTweensOf([this.sprite, this.sprite.scale, this.sprite.position], {
            complete: true,
        });
        if (this.timeLine) {
            this.timeLine.seek(0, true);
            this.timeLine.kill();
            this.timeLine = null;
        }
    }
}
